import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import { useBrand, brandLogo } from "../../lib/brand";
import { PasswordInput } from "../../components/ui/password-input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";

export default function AdminResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const brand = useBrand();
  const logoLight = brandLogo(brand, "light");

  const [state, setState] = useState("checking"); // checking | valid | invalid | success
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    async function verify() {
      try {
        const r = await api.post("/auth/reset-password/verify", { token });
        if (!ignore) setState(r.data?.valid ? "valid" : "invalid");
      } catch {
        if (!ignore) setState("invalid");
      }
    }
    verify();
    return () => { ignore = true; };
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("A nova senha deve ter no mínimo 8 caracteres.");
      return;
    }
    if (pw !== confirm) {
      toast.error("A confirmação não corresponde à nova senha.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setState("success");
      toast.success("Senha atualizada com sucesso.");
      setTimeout(() => nav("/admin/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível redefinir a senha.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#22302E] text-white flex items-center justify-center px-6 font-admin" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          {logoLight ? (
            <img src={logoLight} alt="Azuhomes" className="h-14 mx-auto" />
          ) : (
            <div className="font-display text-4xl tracking-tight text-white">Azuhomes</div>
          )}
          <div className="text-xs uppercase tracking-[0.32em] text-[#3B9FD4] mt-4">Redefinição de senha</div>
        </div>

        <div className="bg-white text-[#22302E] rounded-md p-8 shadow-2xl">
          {state === "checking" && (
            <p className="text-sm text-[#22302E]/70">Validando o link…</p>
          )}

          {state === "invalid" && (
            <div data-testid="reset-invalid">
              <div className="flex items-center gap-2 text-red-600">
                <XCircle className="w-5 h-5" />
                <h1 className="text-xl font-semibold">Link inválido ou expirado</h1>
              </div>
              <p className="text-sm text-[#22302E]/70 mt-3">
                Este link de redefinição não é mais válido. Solicite uma nova recuperação de senha.
              </p>
              <Link to="/admin/esqueci-senha" className="mt-6 inline-flex items-center gap-2 text-sm text-[#0AA89C] hover:text-[#066B63]">
                <ArrowLeft className="w-4 h-4" /> Solicitar novo link
              </Link>
            </div>
          )}

          {state === "success" && (
            <div data-testid="reset-success">
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
                <h1 className="text-xl font-semibold">Senha redefinida</h1>
              </div>
              <p className="text-sm text-[#22302E]/70 mt-3">
                Sua senha foi atualizada. Você será redirecionado(a) para a tela de login em instantes.
              </p>
            </div>
          )}

          {state === "valid" && (
            <form onSubmit={submit} data-testid="reset-password-form">
              <div className="mb-6">
                <div className="flex items-center gap-2 text-[#0AA89C]">
                  <KeyRound className="w-5 h-5" />
                  <h1 className="text-xl font-semibold">Defina uma nova senha</h1>
                </div>
                <p className="text-sm text-[#22302E]/70 mt-1">Utilize no mínimo 8 caracteres, combinando letras e números.</p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="rp-new">Nova senha</Label>
                  <PasswordInput id="rp-new" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} autoComplete="new-password" data-testid="reset-new-password" />
                </div>
                <div>
                  <Label htmlFor="rp-confirm">Confirme a nova senha</Label>
                  <PasswordInput id="rp-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" data-testid="reset-confirm-password" />
                </div>
              </div>
              <Button type="submit" disabled={saving} className="w-full mt-6 bg-[#0AA89C] hover:bg-[#066B63] text-white" data-testid="reset-submit">
                {saving ? "Salvando…" : "Redefinir senha"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
